<script lang="ts">
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import Skeleton from "svelte-skeleton/Skeleton.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import ProductCardBadge from "@shared/svelte/Components/ProductLines/ProductCardBadge.svelte";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import ProductCardDistributors from "@shared/svelte/Components/ProductLines/ProductCardDistributors.svelte";
  import ProductCardSalesDivisions from "@shared/svelte/Components/ProductLines/ProductCardSalesDivisions.svelte";
  import ProductCardPrices from "@shared/svelte/Components/ProductLines/ProductCardPrices.svelte";
  import Sponsored from "@shared/svelte/Components/ProductLines/Sponsored.svelte";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { shouldHideAllDistributorInfo } from "@shared/services/retailer_user_authentication_service";

  import {
    buildProductCardTrackingEntities,
    canShowProductCardBadges,
    filteredVerifiedItemsInStockCount,
    showProductLineDealIcon,
    showFeaturedLabel,
    getBadgeText,
    getIconType,
    getTextColor,
    getProductLineStockInfo,
    getDualProductStockInfo,
  } from "@retailer/services/product_card_service";
  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import { cubicInOut } from "svelte/easing";
  import { slide } from "svelte/transition";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";

  import type { ProductCardPricesEntity } from "@shared/services/event_tracking/entities";
  import type {
    ProductCardInventoryStatus,
    ProductLine,
    InventoryPrice,
  } from "@shared/types/ProductLine";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  const { trackMrcEvents } = EventTracking;

  export let stateParams = {};
  export let index: number;
  export let productLine: ProductLine;
  export let userIsAdmin: boolean | null = null;
  export let inventoryPrices: InventoryPrice[] = [];
  export let loadingPrices: boolean = false;
  export let extraDistributorTooltipClass: string = "";
  export let hidePrice: boolean = false;
  export let distributorOverride: { id: number; name: string } = null;
  export let trackProductIndexLayout: boolean = false;
  export let inventoryStatuses: ProductCardInventoryStatus[] = null;
  export let showFuturePriceAvailability: boolean = true;

  export let checkIfHiddenByCarousel: any = null;
  export let grabbed: boolean = false;
  export let hideSalesDivisions: boolean = false;

  let itemsInStockCount = 0;
  let showItemsInStock: boolean = false;
  let showBadges: boolean = false;
  $: mobileMode = $LayoutStore.isMobile;

  const hasFuturePriceAvailability =
    showFuturePriceAvailability && productLine.future_prices_available;
  const overrideDistributorId = stateParams => {
    return stateParams.distributors?.length ? parseInt(stateParams.distributors[0]) : null;
  };
  const wineCellarStagedRolloutEnabled = stagedRolloutCheck(
    "marketplace__wc_for_geolocation_and_account_based_distributors",
  );
  const canUseWineCellarStockInfo = () => {
    let canUseWineCellar = false;
    if (productLine?.distributor_info?.distributor_id) {
      canUseWineCellar = productLine.distributor_info.uses_wine_cellar;
    } else if (productLine?.dual_product_distributors) {
      const distributorId = overrideDistributorId(stateParams) || distributorOverride?.id;
      if (distributorId) {
        canUseWineCellar = productLine.dual_product_distributors.find(
          d => d.id === distributorId,
        )?.uses_wine_cellar;
      } else {
        canUseWineCellar = productLine.dual_product_distributors.every(d => d.uses_wine_cellar);
      }
    }
    return wineCellarStagedRolloutEnabled && canUseWineCellar;
  };

  const distributorInfoAllowsBadges = canShowProductCardBadges(
    productLine.distributor_info?.distributor_id,
    productLine.dual_product_distributors,
  );

  const productInventory = productLine.products?.map(p => p.inventory)?.flat();
  if (!canUseWineCellarStockInfo()) {
    itemsInStockCount = filteredVerifiedItemsInStockCount(inventoryStatuses, productInventory);
    showItemsInStock = itemsInStockCount > 0;
    showBadges = (showItemsInStock || hasFuturePriceAvailability) && distributorInfoAllowsBadges;
  }

  const hasDeal = showProductLineDealIcon(productLine);

  let productCardPricesEntity: ProductCardPricesEntity;

  const trackingParams = (stockStatus: string | undefined) =>
    buildProductCardTrackingEntities(
      productLine,
      hasDeal,
      index,
      trackProductIndexLayout ? "grid" : null,
      // undefined props get removed from the entity altogether
      inventoryStatuses ? distributorInfoAllowsBadges && showItemsInStock : undefined,
      stockStatus || undefined,
      distributorInfoAllowsBadges && showFuturePriceAvailability
        ? hasFuturePriceAvailability
        : undefined,
      undefined, // Grid view cards never display vintages
      !hideSalesDivisions,
    );
  const detailParams = { productId: productLine.id, indexPosition: index };
  const stateParamsList = ["category", "subcategory", "type", "tagName", "distributors", "search"];

  let hovering: boolean = false;
  const enter = () => {
    hovering = true;
  };
  const leave = () => {
    hovering = false;
  };
  const targetObjectType = "product_card";

  let user: any = $CurrentUserStore;
  const isInAdminUI = window.location.pathname.startsWith("/admin");

  let isAdmin: boolean = userIsAdmin === null ? user.admin : userIsAdmin;
  const disableAdminLinks = () => {
    isAdmin = false;
    hovering = false;
  };

  let hideAllDistributorInfo: boolean = false;
  let productLineDistributorIds: number[];
  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );
  if (productLine?.distributor_info?.distributor_id) {
    productLineDistributorIds = [productLine?.distributor_info?.distributor_id];
  } else if (productLine.dual_product_distributors) {
    productLineDistributorIds = productLine.dual_product_distributors?.map(d => d.id);
  }


  $: hideAllDistributorInfo = shouldHideAllDistributorInfo($RetailerUserAuthenticationsStore, productLineDistributorIds);

  const getProductLineStockStatus = async () => {
    if (!canUseWineCellarStockInfo()) return Promise.resolve(undefined);

    let stockStatus;
    const catalogDistributorId = overrideDistributorId(stateParams) || distributorOverride?.id;
    const fulfillmentMethod =
      $RetailerFulfillmentMethodsStore[productLine.distributor_info.distributor_id] || null;
    if (productLine.distributor_info.distributor_id) {
      stockStatus = await getProductLineStockInfo(
        productInventory,
        productLine.distributor_info.distributor_id,
        productLine.distributor_info.account_number,
        fulfillmentMethod,
        productLine.distributor_info.default_stock_site_id,
      );
    } else if (productLine.dual_product_distributors) {
      stockStatus = await getDualProductStockInfo(
        productInventory,
        productLine.dual_product_distributors,
        catalogDistributorId,
      );
    }
    showItemsInStock = stockStatus?.length > 0;
    showBadges = (showItemsInStock || hasFuturePriceAvailability) && distributorInfoAllowsBadges;
    return stockStatus;
  };
</script>

<style>
  .product-name {
    color: #000;
  }
  .product-name-hover {
    color: #008fbe;
  }
  .truncate-text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .admin-overlay {
    background-color: #008fbe;
    color: white;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
  }
</style>

{#await getProductLineStockStatus() then productLineStockStatus}
  <TrackingContext entities={trackingParams(productLineStockStatus)} let:trackingContext>
    <div
      class="product-card fizz-surface fizz-bordered fizz-bordered--hover"
      style="position: relative; border-radius: 8px; height: 100%;"
      use:trackMrcEvents={[
        trackingContext.entities,
        {
          checkIfHidden: checkIfHiddenByCarousel,
          targetObjectType: targetObjectType,
        },
      ]}
      product-line-id={productLine.id}
      id={`product_line_${productLine.id}`}
      data-testId="product-line-card"
      on:mouseover={enter}
      on:focus={enter}
      on:mouseleave={leave}
    >
      <UIRouterLink
        stopPropagation
        onClickCB={disableAdminLinks}
        stateName={isInAdminUI ? "." : "layout.product_detail"}
        params={detailParams}
        stateParamsToAdd={stateParamsList}
        classes="nounderline-hover fizz-styles"
        useTrackClick={true}
        {targetObjectType}
        {grabbed}
        bind:asyncTrackingParams={productCardPricesEntity}
        style="display: block; height: 100%;"
      >
        <div
          class:fizz-inset-square-8={mobileMode}
          class:fizz-inset-square-16={!mobileMode}
          style="padding-top: 8px;"
          data-testId={`${productLine.is_paid ? "product-card-ad" : null}`}
        >
          <div class="fizz-grid-trio fizz-stack-8">
            {#if hasDeal}
              <div class="fizz-badge-icon deal-marker" data-grid-area="left" data-testid="deal-tag">
                <Icon className="fizz-icon-20" type="DealCircled" height="20" width="20" />
              </div>
            {/if}
            {#if showFeaturedLabel(productLine)}
              <Sponsored size="medium" data-testid="featured-label" data-grid-area="center" />
            {/if}
          </div>
          <ProductImage
            product={productLine}
            width={210}
            height={150}
            fizz={true}
            auto={true}
            aspectRatio={"70"}
            usePlaceholderImage={true}
            className={"fizz-stack-8 fizz-margin-auto"}
          />
          <div class="fizz-stack-16" />
          <div
            class="fizz-text-large fizz-text-strong fizz-stack-8 truncate-text"
            class:product-name-hover={hovering}
            class:product-name={!hovering}
            data-testid="product-card-name"
          >
            {productLine.name}
          </div>

          {#if !hideAllDistributorInfo}
            <ProductCardDistributors
              {productLine}
              {stateParams}
              extraTooltipClass={extraDistributorTooltipClass}
              {distributorOverride}
            />
            {#if !hideSalesDivisions}
              <div class="fizz-stack-8" />
              <ProductCardSalesDivisions {productLine} {stateParams} />
            {/if}

            {#if !hidePrice}
              <div class="fizz-stack-16" />
              {#if loadingPrices}
                <div style="display:inline-block">
                  <Skeleton animate={true} height={28} width={240}>
                    <rect width="200" height="15" x="0" y="10" rx="12" ry="12" />
                  </Skeleton>
                </div>
              {:else}
                <ProductCardPrices
                  {hidePrice}
                  {productLine}
                  {inventoryPrices}
                  bind:trackingEntity={productCardPricesEntity}
                  extraClasses={"fizz-text-strong"}
                />
              {/if}
            {/if}

            {#if showBadges}
              <div class="fizz-stack-4" />
              <div
                data-testId={`badge-container-${productLine.id}`}
                class="fizz-flow-4 fizz-text-deal"
              >
                {#if showItemsInStock}
                  <ProductCardBadge
                    iconType={getIconType(productLineStockStatus)}
                    badgeText={getBadgeText(productLine, productLineStockStatus)}
                    textColor={getTextColor(productLineStockStatus)}
                    testIdSuffix={"in-stock"}
                    subdued={false}
                    surfaceClass={"fizz-surface"}
                  />
                {/if}
                {#if hasFuturePriceAvailability}
                  <ProductCardBadge
                    iconType="DoubleArrow"
                    badgeText="Next month's price available"
                    testIdSuffix="future-prices-available"
                  />
                {/if}
              </div>
            {/if}
          {/if}
        </div>
      </UIRouterLink>

      {#if isAdmin && hovering}
        <div
          data-testid="admin-link"
          class="fizz-text-center fizz-text-strong admin-overlay"
          transition:slide|local={{ duration: 200, easing: cubicInOut }}
        >
          <a
            style="color: #FFF;"
            target="_blank"
            rel="noopener noreferrer"
            href={`/admin/product_lines/${productLine.id}`}
            on:click|stopPropagation
          >
            <Icon type="Edit" height="12" width="12" extraStyles={"display:inline-block;"} /> Admin
          </a>
        </div>
      {/if}
    </div>
  </TrackingContext>
{/await}
